import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { clearAllSessionState } from "@/lib/sessionState";
import { trackLogin, trackLogout } from "@/lib/tracker";

const AuthContext = createContext(null);

// v41d — how long we grace a brief server blip before any UI reacts.
// A 5xx / network error during /auth/me must NOT sign the user out:
// the checkAuth call keeps the previous user object in place and
// retries quietly. Once the outage lasts longer than this threshold
// we expose `reconnecting=true` so the AppShell can show a small
// "Reconnecting…" note instead of the login page.
const RECONNECT_SHOW_AFTER_MS = 4_000;
const RECONNECT_RETRY_MS = 3_000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // v41d — true when /auth/me has been failing with a non-401 (5xx /
  // timeout / network) for more than RECONNECT_SHOW_AFTER_MS. Resets
  // the moment /auth/me responds again (success or a real 401).
  const [reconnecting, setReconnecting] = useState(false);
  const _retryTimer = useRef(null);
  const _blipSince = useRef(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // "Clear on login" contract: if this browser was previously logged
      // in as a different user (or has never been marked), wipe every
      // remembered per-section state so a fresh login starts clean. This
      // covers the case where a session expired or the tab was closed
      // without logging out — the next login won't inherit stale filters.
      try {
        const ownerKey = "kolradar:state:_owner_email";
        const prevOwner = localStorage.getItem(ownerKey);
        if (prevOwner !== data.email) {
          clearAllSessionState();
          localStorage.setItem(ownerKey, data.email || "");
        }
      } catch (_) { /* localStorage disabled — ignore */ }
      // v32: fire a `login` marker on the FIRST successful /auth/me after
      // a sign-in so the admin drill-down timeline has an exact ts for
      // the Google sign-in. Guarded by a one-shot sessionStorage key so
      // we don't re-fire on every re-render / tab-switch within the
      // same session.
      try {
        const key = `kolradar:login_marker:${data.email || "anon"}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          trackLogin();
        }
      } catch (_) { /* sessionStorage disabled — ignore */ }
      setUser(data);
      setLoading(false);
      // Server is back — clear any reconnecting state.
      setReconnecting(false);
      _blipSince.current = null;
      if (_retryTimer.current) { clearTimeout(_retryTimer.current); _retryTimer.current = null; }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // v41d — ONLY a real 401 signs the user out. Everything else
      // (5xx, timeout, 0/network error, CORS, Cloudflare 520/521/522/
      // 524, HTML body) keeps the user in place and schedules a quiet
      // retry. This is the fix for the 8 Oct incident: a 1 s event-loop
      // stall used to kick every open tab back to /login.
      if (status === 401) {
        const genericMessages = new Set([
          "Not authenticated",
          "Session invalid or signed out",
          "Invalid session",
          "Session expired",
          "User not found",
        ]);
        if (detail && !genericMessages.has(detail)) {
          try { sessionStorage.setItem("signin_error", detail); } catch (_) {}
        }
        setUser(null);
        setLoading(false);
        setReconnecting(false);
        _blipSince.current = null;
        if (_retryTimer.current) { clearTimeout(_retryTimer.current); _retryTimer.current = null; }
      } else {
        // Server blip — keep the current user in place. Only flip the
        // reconnecting flag once the blip has lasted long enough that
        // showing anything in the UI is actually useful.
        if (_blipSince.current == null) _blipSince.current = Date.now();
        const blipAge = Date.now() - _blipSince.current;
        if (blipAge >= RECONNECT_SHOW_AFTER_MS) setReconnecting(true);
        // Schedule a quiet retry (coalesced — never more than one
        // outstanding timer at a time).
        if (_retryTimer.current) clearTimeout(_retryTimer.current);
        _retryTimer.current = setTimeout(() => {
          _retryTimer.current = null;
          checkAuth();
        }, RECONNECT_RETRY_MS);
      }
    }
    // v88 — a failed first check (server busy or restarting) keeps `loading` on, so the page
    // waits for the quiet retry instead of sending a signed-in user to the sign-in page.
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
    // Clean up any pending retry timer on unmount.
    return () => {
      if (_retryTimer.current) { clearTimeout(_retryTimer.current); _retryTimer.current = null; }
    };
  }, [checkAuth]);

  const logout = async () => {
    // v32: fire the logout marker BEFORE the API call so the beacon
    // flush catches it even if `/auth/logout` is slow.
    try { trackLogout(); } catch {}
    try { await api.post("/auth/logout"); } catch {}
    // Clear every remembered per-section filter/sort/tab so a fresh
    // login on this browser starts clean (matches product contract).
    clearAllSessionState();
    // v56 — cached API payloads are per user (e.g. admin-only Bot Check): never carry them over.
    try { sessionStorage.clear(); } catch (_) {}
    try { localStorage.removeItem("kolradar:state:_owner_email"); } catch (_) {}
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, reconnecting, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
